import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Chip from "@mui/material/Chip";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import ImageIcon from '@mui/icons-material/Image';
import { Box, Paper, CircularProgress, Avatar, TextField } from "@mui/material"
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import MDButton from "components/MDButton";
import BrokenImage from '@mui/icons-material/BrokenImage';
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import { ServerBaseUrlForStaticImages } from "../../../../constants";

import {
    FormControlLabel,
    Checkbox,
} from "@mui/material";


const AvatarWithLoader = ({ src, alt }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [hasLoadedSuccessfully, setHasLoadedSuccessfully] = useState(false);

    useEffect(() => {
        if (src) {
            setLoading(true);
            setError(false);
            setHasLoadedSuccessfully(false);
        } else {
            // If src is null or empty, consider it an error state immediately
            setLoading(false);
            setError(true);
            setHasLoadedSuccessfully(false);
        }
    }, [src]);

    const handleImageLoad = () => {
        setLoading(false);
        setError(false);
        setHasLoadedSuccessfully(true);
    };

    const handleImageError = () => {
        setLoading(false);
        setError(true);
        setHasLoadedSuccessfully(false);
    };

    const shouldShowLoader = loading && !error && !hasLoadedSuccessfully;
    const hideActualImage = (loading && !hasLoadedSuccessfully) || error;

    if (!src) {
        return (
            <Avatar
                alt={alt}
                sx={{ width: 80, height: 80, margin: "auto", bgcolor: 'grey.300', borderRadius: '8px' }}
                variant="rounded"
            >
                <BrokenImage color="action" sx={{ fontSize: 40 }} />
            </Avatar>
        );
    }

    return (
        <Box sx={{
            position: 'relative',
            width: 80,
            height: 80,
            margin: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e0e0e0', // Add a subtle border
        }}>
            {shouldShowLoader && (
                <CircularProgress size={30} sx={{ position: 'absolute', color: 'info.main' }} />
            )}
            {error && (
                <Avatar
                    alt={alt}
                    sx={{ width: 80, height: 80, margin: "auto", bgcolor: 'error.light', borderRadius: '8px' }}
                    variant="rounded"
                >
                    <BrokenImage sx={{ fontSize: 40, color: 'error.dark' }} />
                </Avatar>
            )}
            {!error && (
                <Avatar
                    src={src}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    sx={{
                        width: '100%', // Make it fill the container
                        height: '100%', // Make it fill the container
                        objectFit: 'cover', // Ensure it covers the area without distortion
                        margin: "auto",
                        variant: "rounded",
                        visibility: hideActualImage ? 'hidden' : 'visible',
                        position: 'absolute',
                        borderRadius: '8px',
                    }}
                    variant="rounded"
                />
            )}
        </Box>
    );
};


function ServiceApprove() {
    const [serviceData, setServiceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [openProfileModal, setOpenProfileModal] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null); // Keep as null by default
    const email = localStorage.getItem("email");
    const navigate = useNavigate();
    const [selectedVendorImages, setSelectedVendorImages] = useState(null); // Initialize as null
    const [profileData, setProfileData] = useState(null); // Initialize as null
    const [vendorId, setVendorId] = useState(null);
    const [checkedImages, setCheckedImages] = useState({});
    const [fullScreenImage, setFullScreenImage] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredServiceData, setFilteredServiceData] = useState([]);

    useEffect(() => {
        disableBackNavigation();
        fetchServiceData();
    }, []);

    useEffect(() => {
        if (searchQuery) {
            const lowerCaseQuery = searchQuery.toLowerCase();
            const newFilteredServices = serviceData.filter(service => {
                return (
                    service.owner_name?.toLowerCase().includes(lowerCaseQuery) ||
                    service.services_type?.toLowerCase().includes(lowerCaseQuery) ||
                    service.email?.toLowerCase().includes(lowerCaseQuery)
                );
            });
            setFilteredServiceData(newFilteredServices);
        } else {
            setFilteredServiceData(serviceData);
        }
        setPage(0);
    }, [searchQuery, serviceData]);

    // Derived states (moved outside of the component body to prevent re-creation on every render if not used in dependency array)
    const experienceCertificates = useMemo(() => {
        if (!selectedVendorImages?.experience_certificates) return [];
        const certs = selectedVendorImages.experience_certificates.replace(/\\/g, "/").split(",");
        // Filter out empty strings or null/undefined if any after splitting
        return certs.filter(c => c.trim() !== '');
    }, [selectedVendorImages]);

    const idProofPhotos = useMemo(() => {
        if (!selectedVendorImages?.idproof_photo) return [];
        const proofs = selectedVendorImages.idproof_photo.replace(/\\/g, "/").split(",");
        return proofs.filter(p => p.trim() !== '');
    }, [selectedVendorImages]);

    const imageKeys = useMemo(() => {
        const keys = [];
        if (selectedVendorImages?.work_place_gps) keys.push("work_place_gps");
        if (selectedVendorImages?.profile_photo) keys.push("profile_photo");
        return keys;
    }, [selectedVendorImages]);

    const allKeys = useMemo(() => {
        const keys = [
            ...imageKeys,
            ...experienceCertificates.map((_, index) => `experience_certificates-${index}`),
            ...idProofPhotos.map((_, index) => `idproof_photo-${index}`)
        ];
        return keys;
    }, [imageKeys, experienceCertificates, idProofPhotos]);


    useEffect(() => {
        // Only initialize checkedImages if the modal is opening and selectedVendorImages is available
        if (openViewModal && selectedVendorImages) {
            const initialCheckedState = {};
            allKeys.forEach((key) => {
                initialCheckedState[key] = false; // Default all checkboxes to false
            });
            setCheckedImages(initialCheckedState);
        } else if (!openViewModal) {
            // Reset checkedImages when the modal closes
            setCheckedImages({});
        }
    }, [openViewModal, selectedVendorImages, allKeys]);


    const handleCheckboxChange = (key, checked) => {
        setCheckedImages((prev) => ({
            ...prev,
            [key]: checked
        }));
    };

    const allChecked = useMemo(() => {
        return allKeys.length > 0 && allKeys.every((key) => checkedImages[key] === true);
    }, [checkedImages, allKeys]);


    const fetchServiceData = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/services_register/fetch_pending_services`, {
                params: { email },
            });
            setServiceData(res.data.services || []);
        } catch (error) {
            console.error("Error fetching service data:", error);
            Swal.fire("Error", "Failed to load service data. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleViewMedia = (vendor) => {
        setVendorId(vendor.id);
        setSelectedVendorImages(vendor); // Set the full vendor object for image paths
        setOpenViewModal(true);
    };

    const handleImageClick = (imageUrl) => {
        setFullScreenImage(imageUrl);
    };

    const handleViewProfile = (vendor) => {
        setProfileData(vendor);
        setOpenProfileModal(true);
    };

    const handleApprove = async () => {
        // Close the image modal first
        setOpenViewModal(false);
        // Reset checked images immediately after closing the modal, before the Swal.fire prompt
        setCheckedImages({});

        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to approve this service?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, approve!",
            cancelButtonText: "No, cancel",
        });

        if (result.isConfirmed) {
            try {
                const response = await api.post(
                    `services_register/approve_services/${vendorId}`
                );
                if (response.data.success) {
                    Swal.fire({
                        title: "Service Approved Successfully!",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                    });
                    fetchServiceData();
                } else {
                    Swal.fire({
                        title: "Error",
                        text: response.data.message,
                        icon: "error",
                    });
                }
            } catch (error) {
                console.error("Error approving service:", error);
                Swal.fire({
                    title: "Error",
                    text: "An error occurred while approving the service.",
                    icon: "error",
                });
            }
        } else {
            Swal.fire({
                title: "Cancelled",
                text: "The service approval has been cancelled.",
                icon: "info",
                timer: 1500,
                showConfirmButton: false,
            });
        }
    };

    const handleReject = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "Do you really want to reject this service?",
            input: "text",
            inputLabel: "Reason",
            inputValidator: (value) => {
                if (!value) {
                    return "You need to write something!";
                }
            },
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Confirm!",
            cancelButtonText: "No, cancel",
        });

        if (result.isConfirmed) {
            try {
                const rejectReasonResponse = await api.put(
                    `services_register/reject_service_reason/${id}`,
                    { reason: result.value }
                );
                const rejectServiceResponse = await api.post(`/services_register/reject_services/${id}`);

                if (rejectReasonResponse.data.success && rejectServiceResponse.data.success) {
                    Swal.fire({
                        title: "Service Provider Not Approved!",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                    });
                    fetchServiceData();
                } else {
                    Swal.fire({
                        title: "Error",
                        text: rejectReasonResponse.data.message || rejectServiceResponse.data.message,
                        icon: "error",
                    });
                }
            } catch (error) {
                console.error("Error rejecting service:", error);
                Swal.fire({
                    title: "Error",
                    text: "An error occurred while rejecting the service Provider.",
                    icon: "error",
                });
            }
        } else {
            Swal.fire({
                title: "Cancelled",
                text: "The Service Provider approval has been cancelled.",
                icon: "info",
                timer: 1500,
                showConfirmButton: false,
            });
        }
    };

    const handleRowsPerPageChange = (event, newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const paginatedTableData = useMemo(() => {
        return filteredServiceData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [filteredServiceData, page, rowsPerPage]);


    const columns = useMemo(() => [
        {
            Header: "S.No",
            accessor: "s_no",
            align: "center",
            Cell: ({ row }) => (page * rowsPerPage) + row.index + 1,
        },
        { Header: "Name", accessor: "owner_name", sortable: true },
        { Header: "Service Type", accessor: "services_type", sortable: true },
        { Header: "Email", accessor: "email", sortable: true },
        {
            Header: "Options",
            accessor: "options",
            Cell: ({ row }) => (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                        color="info"
                        onClick={() => handleViewProfile(row.original)}
                        title="View Profile"
                    >
                        <VisibilityIcon />
                    </IconButton>
                    <IconButton
                        color="success"
                        onClick={() => handleViewMedia(row.original)}
                        title="View Images"
                    >
                        <ImageIcon /> {/* Changed from CheckCircleIcon for image icon */}
                    </IconButton>

                    <IconButton
                        color="error"
                        onClick={() => handleReject(row.original.id)}
                        title="Reject"
                    >
                        <CancelIcon />
                    </IconButton>
                </div>
            ),
        },
    ], [page, rowsPerPage]); // Removed handleViewProfile, handleViewMedia, handleReject from useMemo dependencies as they are stable functions or their dependencies are already in columns.

    return (
        <DashboardLayout>
            <MDBox display="flex" justifyContent="flex-end" alignItems="center">
                <LogoutButton
                    onLogout={() => {
                        navigate("/login");
                    }}
                />
            </MDBox>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                            >
                                <MDTypography variant="h6" color="white">
                                    Approve Service Provider
                                </MDTypography>
                            </MDBox>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" pl={3} pt={2}>
                                <TextField
                                    label="Search services..."
                                    variant="outlined"
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    sx={{ width: '300px' }}
                                />
                            </MDBox>
                            <MDBox p={3}>
                                {loading ? (
                                    <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                                        <CircularProgress color="info" />
                                    </MDBox>
                                ) : (
                                    <DataTable
                                        table={{ columns: columns, rows: paginatedTableData }}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                                        showTotalEntries={true}
                                        noEndBorder
                                        pagination={{
                                            variant: "contained",
                                            color: "dark",
                                            page: page,
                                            onPageChange: handlePageChange,
                                            rowsPerPage: rowsPerPage,
                                            onRowsPerPageChange: handleRowsPerPageChange,
                                            count: filteredServiceData.length,
                                        }}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>


            {/* Dialog for viewing the images */}
            <Dialog open={openViewModal} onClose={() => setOpenViewModal(false)} fullWidth maxWidth="md">
                <DialogTitle
                    sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.25rem",
                        py: 2,
                    }}
                >
                    Service Provider Images
                </DialogTitle>

                <DialogContent dividers sx={{ backgroundColor: "#f9f9f9" }}>
                    <Grid container spacing={2}>
                        {/* Render Main Images */}
                        {imageKeys.map((key) => {
                            const imageSrc = selectedVendorImages?.[key];
                            const imageUrl = imageSrc ? `${ServerBaseUrlForStaticImages}services_register/media/${imageSrc}` : null;

                            return (
                                <Grid item xs={12} sm={4} key={key}>
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            backgroundColor: "#fff",
                                            padding: 1,
                                            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            onClick={() => imageUrl && handleImageClick(imageUrl)}
                                            sx={{ cursor: imageUrl ? "pointer" : "default", mb: 1 }}
                                        >
                                            <AvatarWithLoader
                                                src={imageUrl}
                                                alt={key.replace("_", " ").toUpperCase()}
                                            />
                                        </Box>

                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <Checkbox
                                                checked={checkedImages[key] || false}
                                                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                                                sx={{
                                                    '& .MuiSvgIcon-root': { fontSize: 24, color: red[500] },
                                                    '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' },
                                                    mb: 0.5,
                                                }}
                                            />
                                            <MDTypography
                                                variant="caption"
                                                align="center"
                                                fontWeight="medium"
                                                color="primary"
                                                sx={{
                                                    fontSize: key.length > 20 ? '0.72rem' : '0.8rem',
                                                    lineHeight: 1.3,
                                                    wordBreak: 'break-word',
                                                    px: 1,
                                                }}
                                            >
                                                {key.replace("_", " ").toUpperCase()}
                                            </MDTypography>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}

                        {/* Render Experience Certificates */}
                        {experienceCertificates.map((image, index) => {
                            const key = `experience_certificates-${index}`;
                            const imageUrl = image ? `${ServerBaseUrlForStaticImages}services_register/media/${image}` : null;

                            return (
                                <Grid item xs={12} sm={4} key={key}>
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            backgroundColor: "#fff",
                                            padding: 1,
                                            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            onClick={() => imageUrl && handleImageClick(imageUrl)}
                                            sx={{ cursor: imageUrl ? "pointer" : "default", mb: 1 }}
                                        >
                                            <AvatarWithLoader
                                                src={imageUrl}
                                                alt={`EXPERIENCE CERTIFICATE ${index + 1}`}
                                            />
                                        </Box>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <Checkbox
                                                checked={checkedImages[key] || false}
                                                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                                                sx={{
                                                    '& .MuiSvgIcon-root': { fontSize: 24, color: red[500] },
                                                    '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' },
                                                    mb: 0.5,
                                                }}
                                            />
                                            <MDTypography
                                                variant="caption"
                                                align="center"
                                                fontWeight="medium"
                                                color="primary"
                                                sx={{ fontSize: '0.72rem', lineHeight: 1.3, px: 1 }}
                                            >
                                                EXPERIENCE CERTIFICATE {index + 1}
                                            </MDTypography>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}

                        {/* Render ID Proof Photos */}
                        {idProofPhotos.map((image, index) => {
                            const key = `idproof_photo-${index}`;
                            const imageUrl = image ? `${ServerBaseUrlForStaticImages}services_register/media/${image}` : null;

                            return (
                                <Grid item xs={12} sm={4} key={key}>
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            backgroundColor: "#fff",
                                            padding: 1,
                                            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            onClick={() => imageUrl && handleImageClick(imageUrl)}
                                            sx={{ cursor: imageUrl ? "pointer" : "default", mb: 1 }}
                                        >
                                            <AvatarWithLoader
                                                src={imageUrl}
                                                alt={`ID PROOF ${index + 1}`}
                                            />
                                        </Box>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <Checkbox
                                                checked={checkedImages[key] || false}
                                                onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                                                sx={{
                                                    '& .MuiSvgIcon-root': { fontSize: 24, color: red[500] },
                                                    '&.Mui-checked .MuiSvgIcon-root': { color: '#1976d2' },
                                                    mb: 0.5,
                                                }}
                                            />
                                            <MDTypography
                                                variant="caption"
                                                align="center"
                                                fontWeight="medium"
                                                color="primary"
                                                sx={{ fontSize: '0.72rem', lineHeight: 1.3, px: 1 }}
                                            >
                                                ID PROOF {index + 1}
                                            </MDTypography>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                        {allKeys.length === 0 && (
                            <Grid item xs={12}>
                                <MDTypography variant="body1" color="textSecondary" align="center" py={3}>
                                    <WarningIcon sx={{ fontSize: 40, color: red[500], mb: 1 }} />
                                    <br />
                                    No images available for this service provider.
                                </MDTypography>
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <MDButton
                        onClick={() => {
                            setOpenViewModal(false);
                            setCheckedImages({}); // Reset checked images when modal is closed
                            setSelectedVendorImages(null); // Clear selected vendor images
                            setVendorId(null); // Clear vendor ID
                        }}
                        color="primary"
                    >
                        Close
                    </MDButton>
                    <MDButton onClick={handleApprove} color="success" disabled={!allChecked}>
                        Approve
                    </MDButton>
                </DialogActions>
            </Dialog>


            {/* Full-Screen Image Modal */}
            <Dialog open={Boolean(fullScreenImage)} onClose={() => setFullScreenImage(null)} maxWidth="xl">
                <DialogContent>
                    <img
                        src={fullScreenImage}
                        alt="Full screen"
                        style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFullScreenImage(null)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openProfileModal}
                onClose={() => {
                    setOpenProfileModal(false);
                    setProfileData(null); // Clear profile data on close
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle
                    sx={{
                        backgroundColor: "#673ab7",
                        color: "#ffffff",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.25rem",
                        py: 2,
                    }}
                >
                    Service Profile Details
                </DialogTitle>

                <DialogContent sx={{ padding: 3, backgroundColor: "#f5f5f5" }}>
                    {profileData ? (
                        <Paper
                            elevation={3}
                            sx={{
                                padding: "24px",
                                borderRadius: "12px",
                                backgroundColor: "white",
                            }}
                        >
                            <Grid container spacing={2}>
                                {/* Left Column */}
                                <Grid item xs={12} md={6}>
                                    {[
                                        { label: "Name", value: profileData.owner_name },
                                        { label: "Email", value: profileData.email },
                                        { label: "Mobile", value: profileData.mobile_number },
                                        { label: "Service Type", value: profileData.services_type },
                                        { label: "Details", value: profileData.additional_details },
                                        { label: "Work Location", value: profileData.worklocation },
                                        { label: "Passbook Name", value: profileData.passbook_name },
                                        { label: "Bank Name", value: profileData.bank_name },
                                        { label: "Branch Name", value: profileData.branch_name },
                                    ].map((item, idx) => (
                                        <Box key={idx} mb={1.5}>
                                            <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                                                {item.label}:
                                            </MDTypography>
                                            <MDTypography variant="body2">{item.value || "N/A"}</MDTypography>
                                        </Box>
                                    ))}
                                </Grid>

                                {/* Right Column */}
                                <Grid item xs={12} md={6} >
                                    <Box mt={2} mb={2}>
                                        <MDTypography
                                            variant="h6"
                                            sx={{ fontWeight: "bold", color: "#512da8", mb: 1 }}
                                        >
                                            🛠 Specializations
                                        </MDTypography>

                                        {(() => {
                                            let parsedSpecializations = null; // Changed to null
                                            try {
                                                parsedSpecializations =
                                                    typeof profileData.subservices === "string"
                                                        ? JSON.parse(profileData.subservices)
                                                        : profileData.subservices;
                                            } catch (e) {
                                                console.error("Failed to parse specializations:", e);
                                                // Keep parsedSpecializations as null if parsing fails
                                            }

                                            return parsedSpecializations && typeof parsedSpecializations === "object" && Object.keys(parsedSpecializations).length > 0 ? (
                                                Object.entries(parsedSpecializations).map(([type, services], i) => (
                                                    <Box key={i} ml={2} mt={2} p={2} sx={{
                                                        backgroundColor: "#f3e5f5",
                                                        borderRadius: "10px",
                                                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)"
                                                    }}>
                                                        <MDTypography
                                                            variant="subtitle5"
                                                            sx={{
                                                                fontWeight: "bold",
                                                                color: "#6a1b9a",
                                                                mb: 1,
                                                                textTransform: "capitalize",
                                                                letterSpacing: 0.5
                                                            }}
                                                        >
                                                            {type}
                                                        </MDTypography>
                                                        <ul style={{ marginLeft: 20, padding: 0 }}>
                                                            {/* Safely access services array and join */}
                                                            {Array.isArray(services) && services[0] ?
                                                                String(services[0])
                                                                    .split(",")
                                                                    .map((item, idx) => (
                                                                        <li
                                                                            key={idx}
                                                                            style={{
                                                                                fontSize: "0.95rem",
                                                                                color: "#4a4a4a",
                                                                                marginBottom: 4,
                                                                                lineHeight: 1.6,
                                                                            }}
                                                                        >
                                                                            ✅ {item.trim()}
                                                                        </li>
                                                                    )) : (
                                                                    <MDTypography variant="body2" color="text.secondary">N/A</MDTypography>
                                                                )}
                                                        </ul>
                                                    </Box>
                                                ))
                                            ) : (
                                                <MDTypography variant="body2" color="text.secondary">
                                                    No specializations available.
                                                </MDTypography>
                                            );
                                        })()}
                                    </Box>


                                    {[
                                        { label: "PAN Card", value: profileData.pan_card_number },
                                        { label: "Experience (Years)", value: profileData.experiance },
                                        { label: "Home Address", value: profileData.homeaddress },
                                    ].map((item, idx) => (
                                        <Box key={idx} mb={1.5}>
                                            <MDTypography variant="subtitle2" color="primary" fontWeight="bold">
                                                {item.label}:
                                            </MDTypography>
                                            <MDTypography variant="body2">{item.value || "N/A"}</MDTypography>
                                        </Box>
                                    ))}
                                </Grid>
                            </Grid>

                            {/* Status Badge */}
                            <Box
                                sx={{
                                    mt: 3,
                                    textAlign: "center",
                                    padding: "10px 16px",
                                    backgroundColor: profileData.approved_status === "Approved" ? "#4caf50" :
                                        profileData.approved_status === "Rejected" ? "#f44336" : "#ff9800", // Orange for pending
                                    color: "white",
                                    borderRadius: "8px",
                                }}
                            >
                                <MDTypography variant="subtitle1" fontWeight="bold">
                                    Status: {profileData.approved_status || "N/A"}
                                </MDTypography>
                                {profileData.reason && profileData.approved_status === "Rejected" && (
                                    <MDTypography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                        Reason: {profileData.reason}
                                    </MDTypography>
                                )}
                            </Box>
                        </Paper>
                    ) : (
                        <MDTypography
                            variant="body1"
                            sx={{ color: "#d32f2f", textAlign: "center" }}
                        >
                            No service data available.
                        </MDTypography>
                    )}
                </DialogContent>

                {/* Footer */}
                <DialogActions
                    sx={{
                        backgroundColor: "#e3f2fd",
                        justifyContent: "center",
                        py: 2,
                    }}
                >
                    <Button
                        onClick={() => {
                            setOpenProfileModal(false);
                            setProfileData(null); // Clear profile data on close
                        }}
                        variant="contained"
                        sx={{
                            backgroundColor: "#673ab7",
                            color: "#fff",
                            fontWeight: "bold",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "#512da8",
                            },
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default ServiceApprove;